// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ExitThin } from '@bloomberg/media-ui-icons';

import { useSlug } from '../hooks/useSlug';
import { IconButton } from '../IconButton';
import { NotificationIcons } from '../NotificationBanner';
import Portal from '../Portal';
import { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './toast.module.css';

export type CloseReason = 'EscapeKey' | 'ExitClick' | 'AutoHideTimer';

export type ToastProps = GlobalProps & {
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'right' | 'center';
  };
  maxSize?: 'default' | 'medium' | 'large' | 'full';
  autoHideDuration?: number /* Num. milliseconds which toast shows. If autoHideDuration=0, toast shows forever. */;
  disableExit?: boolean;
  Icon?: React.ReactNode;
  children?: React.ReactNode;
  open: boolean;
  onClose?: (reason: CloseReason) => void;
  variant?: 'error' | 'success' | 'warning' | 'message';
  inline?: boolean;
};

// ------------------------------------------------------------
// Inner Component which shows/hides
// ------------------------------------------------------------
export function InnerToast(
  props: ToastProps & {
    hideInnerContent: boolean;
    handleExit: (reason: CloseReason) => void;
  }
) {
  const {
    name = 'Toast-Inner',
    position = {
      vertical: 'bottom',
      horizontal: 'left',
    },
    autoHideDuration = 0,
    disableExit = false,
    maxSize = 'default',
    hideInnerContent,
    Icon: passedIcon,
    children,
    handleExit,
    variant = 'message',
  } = props;

  const slugName = useSlug(name);

  const Icon =
    passedIcon ||
    (variant === 'message' ? undefined : NotificationIcons[variant]);

  // hold a reference to auto-hide timer
  const timer = useRef<number>();

  // main outer classes
  const wrapperClasses = classes(
    [
      styles.wrapper,
      styles[position.horizontal],
      styles[position.vertical],
      styles[maxSize],
      styles[variant],
      {
        [styles.disableExit]: disableExit,
      },
    ],
    {
      ...props,
      name,
    }
  );

  const wrappedExit = useCallback((reason: CloseReason) => {
    return function handler(_ev: React.MouseEvent<HTMLButtonElement>) {
      return handleExit(reason);
    };
  }, []);

  // autohide effect
  useEffect(() => {
    if (autoHideDuration > 0) {
      timer.current = window.setTimeout(() => {
        handleExit('AutoHideTimer');
      }, autoHideDuration);
    }

    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
      }
    };
  }, [autoHideDuration]);

  if (hideInnerContent) {
    return null;
  }

  return (
    <div
      className={wrapperClasses}
      aria-live="polite"
      data-toast="true"
      data-component={slugName}
    >
      {Icon && (
        <div className={styles.icon} data-icon="true">
          {Icon}
        </div>
      )}

      <div className={styles.contentWrapper}>{children}</div>

      {!disableExit && (
        <span className={styles.iconButtonWrapper}>
          <IconButton
            onClick={wrappedExit('ExitClick')}
            className={styles.iconButton}
          >
            <ExitThin />
          </IconButton>
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Toast Component
// ------------------------------------------------------------
export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  function ToastComponent(props, ref) {
    const {
      name = 'Toast',
      position = {
        vertical: 'bottom',
        horizontal: 'left',
      },
      open = false,
      onClose,
      inline = false,
    } = props;

    const slugName = useSlug(name);

    // Our own internal closed state so that if the
    // consumer does not provide an onClose handler, we can
    // still hide the toast on exit
    const [closed, setClosed] = useState(false);

    // Handle exit events by calling the user defined onClose if it
    // exists, and then setting our own internal state to closed
    const handleExit = useCallback((reason: CloseReason) => {
      // notify upstream
      if (onClose) {
        onClose(reason);
      }
      setClosed(true);
    }, []);

    // flag if toast should be rendered
    const hideInnerContent = !open || closed;

    const outerClasses = classes(
      [styles.outer, styles[position.horizontal], styles[position.vertical]],
      props
    );

    // clean up closed if !open is handled
    useEffect(() => {
      if (!open && closed) {
        setClosed(false);
      }
    }, [open, closed]);

    // TODO: Extract on next usage
    useEffect(() => {
      function keydownEventHandler(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          // call upstream func if available

          handleExit('EscapeKey');
        }
      }

      window.addEventListener('keydown', keydownEventHandler, {
        passive: true,
      });

      return () => window.removeEventListener('keydown', keydownEventHandler);
    }, []);

    if (inline) {
      return (
        <InnerToast
          {...props}
          handleExit={handleExit}
          hideInnerContent={hideInnerContent}
        />
      );
    }

    return (
      <Portal>
        <div className={outerClasses} data-component={slugName} ref={ref}>
          <InnerToast
            {...props}
            handleExit={handleExit}
            hideInnerContent={hideInnerContent}
          />
        </div>
      </Portal>
    );
  }
);
